import { useCreateQuestion } from 'hooks/useInteractivityQnA';
import React from 'react';
import { css } from 'emotion';
import { useForm } from 'react-hook-form';
import { ControlledFieldInput, FieldLabel } from 'components/encore/form/Field';
import { Modal } from '@spotify-internal/anchor-ds';
import { useT } from 'client/i18n/hooks/useT';
import { SubmitButton } from './SubmitButton';

type FormData = { text: string };

type Props = {
  episodeId: string;
  onSuccess: () => void;
  defaultValues: FormData;
};

export function InteractiveQuestionForm({
  episodeId,
  onSuccess,
  defaultValues,
}: Props) {
  const { createQuestion } = useCreateQuestion();
  const {
    control,
    formState: {
      isSubmitting,
      isDirty,
      errors: { text: error },
    },
    handleSubmit,
  } = useForm<FormData>({ defaultValues });
  const t = useT();

  const onSubmit = async (formValues: FormData) => {
    const { text } = formValues;
    try {
      await createQuestion({ episodeId, text });
    } catch (e) {
      throw new Error(`Could not create question: ${e}`);
    } finally {
      onSuccess();
    }
  };

  return (
    <form
      className={css`
        padding-top: 28px;
      `}
      onSubmit={e => {
        e.stopPropagation();
        return handleSubmit(onSubmit)(e);
      }}
    >
      <FieldLabel htmlFor="text">
        {t(
          'EpisodeEditorPublish.InteractiveQuestionForm.QuestionFieldLabel',
          'Ask your listeners a question or post a statement.',
          'Interactive question field label'
        )}
      </FieldLabel>
      <ControlledFieldInput
        name="text"
        type="text"
        // @ts-ignore
        control={control}
        rules={{ required: 'Required', maxLength: 140 }}
        showCharacterCount
      />
      {error && (
        <Modal.Error>
          {t(
            'EpisodeEditorPublish.InteractiveQuestionForm.ErrorMessage',
            'Sorry, looks like something went wrong. Try again?',
            'Interactive question save error message'
          )}
        </Modal.Error>
      )}
      <Modal.Footer
        className={css`
          text-align: center;
        `}
      >
        <SubmitButton isSubmitting={isSubmitting} pristine={!isDirty} />
      </Modal.Footer>
    </form>
  );
}
